import { useCallback, useRef, useState } from 'react';
import { Order } from 'components/table/types';
import { DEFAULT_SORT_ORDER } from 'constants/pagination.constants';

export type SortState = {
	sortBy: string;
	sortOrder: Order;
};

type FetchPage<TContext> = (page: number, sortState: SortState, context?: TContext) => Promise<void>;

type UseServerPaginationArgs<TContext> = {
	initialSortBy: string;
	initialSortOrder?: Order;
	initialPage?: number;
	fetchPage: FetchPage<TContext>;
};

export const useServerPagination = <TContext = undefined>({
	initialSortBy,
	initialSortOrder = DEFAULT_SORT_ORDER,
	initialPage = 0,
	fetchPage,
}: UseServerPaginationArgs<TContext>) => {
	const [currentPage, setCurrentPage] = useState(initialPage);
	const [sortState, setSortStateState] = useState<SortState>({
		sortBy: initialSortBy,
		sortOrder: initialSortOrder,
	});
	const [isLoading, setIsLoading] = useState(false);
	const sortStateRef = useRef<SortState>({ sortBy: initialSortBy, sortOrder: initialSortOrder });

	const setSortState = useCallback((next: SortState) => {
		sortStateRef.current = next;
		setSortStateState(next);
	}, []);

	const runFetch = useCallback(
		async (page: number, sortOverride?: SortState, context?: TContext) => {
			const nextSort = sortOverride || sortStateRef.current;
			setIsLoading(true);
			setCurrentPage(page);
			try {
				await fetchPage(page, nextSort, context);
			} finally {
				setIsLoading(false);
			}
		},
		[fetchPage]
	);

	const handlePageChange = useCallback(
		(page: number, context?: TContext) => runFetch(page, undefined, context),
		[runFetch]
	);

	const handleSortChange = useCallback(
		(sortBy: string, sortOrder: Order, context?: TContext) => {
			const nextSort = { sortBy, sortOrder };
			setSortState(nextSort);
			return runFetch(0, nextSort, context);
		},
		[runFetch, setSortState]
	);

	return { currentPage, sortState, isLoading, runFetch, handlePageChange, handleSortChange };
};
